import { Box, List, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { SwipeableList } from '@sandstreamdev/react-swipeable-list';
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import type { InfiniteData } from '@zg-rentals/react-query5';
import type { SatelliteWebLandlordGetLatestConversationsForInboxResponseDto } from '@zg-rentals/ts-satellite-client';
import { SatelliteConversationReadRepliedStatusTypeV2 } from '@zg-rentals/ts-satellite-client';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import { emitConversationItemSelectEvent } from '../../constants/clickstreamEvents/conversationItem';
import {
  MAX_DEEP_LINKED_CONVERSATION_AUTO_FETCH_PAGES,
  MAX_OLD_CONVERSATION_DAYS,
} from '../../constants/ConfigConstants';
import {
  inboxPageConversationIdToFilterByStore,
  inboxPageConversationSearchTextStore,
  inboxPageIsLoadingStore,
  inboxPageShowEmptyContainerStore,
} from '../../lib/inboxSearchListStores';
import useDevices from '../../hooks/useDevices';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import ConditionalWrapper from '../ConditionalWrapper';
import ConversationItemContainer from './components/ConversationItemContainer';
import EmptyContainer from './components/EmptyContainer';
import ViewLeads from '../ViewLeads';
import { ConversationItemSkeleton, ConversationListSkeleton } from './skeleton';
import isFeedId from '../../utils/isFeedId';
import { logError } from '@zg-rentals/log-error';
import { throttle } from '@zg-rentals/zdash';
import { useAuth } from '../../AuthProvider';
import { scrollbarHoverStyles } from '../../styles/scrollbar';
import { SatelliteWebLandlordConversationHandler_getLatestConversationsForInbox } from '@zg-rentals/ts-satellite-client';
import { useSatelliteWebLandlordConversationHandler_getCachedConversationCounts } from '@zg-rentals/ts-satellite-client-react-query';
import useInboxConversationsQuery, {
  buildRequestParams,
  getInboxConversationsQueryKey,
  type InboxConversation,
} from '../../hooks/useInboxConversationsQuery';
import useRealtimeUpdates, {
  LIST_MUTATING_EVENT_TYPES,
  useGuardedRealtimeUpdate,
} from '../../hooks/useRealtimeUpdates';
import { useInvalidateMessageCounts } from '../../hooks/useInvalidateMessageCounts';

export type ConversationSelectArgs = {
  selectedConversationId: string;
  selectedListingAlias: string;
};

export type ConversationListConversation = InboxConversation;
export type ConversationListConversations = Array<InboxConversation>;

const NEXT_PAGE_SCROLL_THRESHOLD_PX = 200;

// onScroll fires many times per second during momentum scrolling; throttle the near-bottom check to
// run at most once per this interval (leading + trailing, so it reacts immediately and once more
// after the user settles).
const NEXT_PAGE_SCROLL_THROTTLE_MS = 150;

const LOAD_MORE_ERROR_MESSAGE = "Couldn't load more conversations. Please refresh the page to try again.";
// Stable key on the enqueued Toast element so we can find and dismiss this specific toast later
// (ToastProvider preserves the node's key and exposes it via `toasts`).
const LOAD_MORE_ERROR_TOAST_KEY = 'load-more-conversations-error-toast';

// Chrome doesn't apply prefers-reduced-motion to programmatic smooth scrolls (Firefox does), so the
// preference has to be honored explicitly before animating.
const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ConversationList = () => {
  const queryClient = useQueryClient();
  const { changeConversation, clearConversation, getFiltersQueryString, updatePath } = useInboxRouterHelper();
  const { isDesktopView, isMobileView } = useDevices();
  const { enqueueToast, dismiss, toasts } = useToast();

  const {
    listingAlias,
    clientTimeZone,
    daysBack,
    isByProperty,
    folder,
    renterProgressStatus,
    readRepliedStatus,
    conversationId,
    mobileView,
  } = useContext(InboxPageContext);

  const [isLoadingOlderConversations, setIsLoadingOlderConversations] = useState(false);
  const [shouldShowSkeleton, setShouldShowSkeleton] = useState(false);
  const [hasMoreThan5Listings, setHasMoreThan5Listings] = useState(false);
  const hasLoadedOlderConversations = !!daysBack;

  // The scrollable list element, measured by the bootstrap-fill effect below.
  const listRef = useRef<HTMLUListElement | null>(null);

  // Monotonic token that increments on any URL-bearing context change. Async
  // work (e.g. fetchOlderConversations) captures the token at start and
  // compares against the latest value before pushing URL updates, so the user's
  // new navigation state isn't clobbered by stale closure values when the await
  // resolves. useLayoutEffect (not useEffect) so the token updates synchronously
  // during commit, before any awaited promise continuation can observe a stale
  // value as a microtask.
  const navTokenRef = useRef(0);
  useLayoutEffect(() => {
    navTokenRef.current++;
  }, [folder, conversationId, listingAlias, isByProperty, readRepliedStatus, renterProgressStatus, mobileView]);

  const { user } = useAuth();
  const { isEnterpriseCustomer } = user || {};
  const inboxPageConversationIdToFilterBy = inboxPageConversationIdToFilterByStore.useValue();
  const inboxPageConversationSearchText = inboxPageConversationSearchTextStore.useValue();
  const inboxPageShowEmptyContainer = inboxPageShowEmptyContainerStore.useValue();

  const {
    data: conversationsData,
    error,
    isLoading: loading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    refetch,
  } = useInboxConversationsQuery({
    conversationIdToFilterBy: inboxPageConversationIdToFilterBy || undefined,
  });

  // Shares the StatusSelector badge's cache entry (same query key), so this observes the count the
  // badge displays rather than issuing a second request.
  const { data: conversationCounts } = useSatelliteWebLandlordConversationHandler_getCachedConversationCounts({
    meta: {
      tags: ['satellite:conversationCounts'],
      context: {
        api: 'getCachedConversationCounts',
        details: 'ConversationList - Error getting unread counts',
      },
    },
  });
  const unreadConversationCount = conversationCounts?.unreadCount || 0;

  const invalidateMessageCounts = useInvalidateMessageCounts();
  const handleRealtimeUpdate = useGuardedRealtimeUpdate(
    error,
    useCallback(() => {
      refetch();
      invalidateMessageCounts();
    }, [refetch, invalidateMessageCounts]),
  );

  useRealtimeUpdates({
    activeConversationId: conversationId,
    clientTimeZone,
    onRealtimeUpdate: handleRealtimeUpdate,
    // subscribeToMessages delivers every frame from every subscribed topic, and one message fans
    // out into several event types — restrict to list-level types so a single received message
    // refetches the list once, not once per frame. The open conversation's `newMessage` is owned
    // by useMessageListRealtimeUpdates.
    refetchOnEventTypes: LIST_MUTATING_EVENT_TYPES,
  });

  useEffect(() => {
    const conversations = conversationsData?.conversations;
    const hasSelectedNonFeedConversation = conversationId && (!listingAlias || !isFeedId(listingAlias));

    if (!loading && (!conversations?.length || hasSelectedNonFeedConversation)) {
      inboxPageIsLoadingStore.set(false);
    }
  }, [loading, listingAlias, conversationId, conversationsData]);

  const refetchConversationsAsNetworkOnly = async () => {
    setShouldShowSkeleton(true);
    await refetch();
    setShouldShowSkeleton(false);
  };

  useEffect(() => {
    if (!loading) refetchConversationsAsNetworkOnly();
  }, [folder]);

  useEffect(() => {
    if (readRepliedStatus === SatelliteConversationReadRepliedStatusTypeV2.UNREAD) {
      refetchConversationsAsNetworkOnly();
    }
  }, [readRepliedStatus]);

  useEffect(() => {
    const conversations = conversationsData?.conversations;
    const totalListingNumber = Number(user?.summary?.totalListingCount);
    // A 660-day lookback across many listings is expensive, so it's only auto-run for small
    // accounts. Widening scoped to a single property is cheap, so always allow it there — this is
    // the path the empty state tells 5+-listing landlords to take to reach older conversations.
    // A missing/partial summary yields NaN; treat that as "few listings" so it doesn't suppress
    // the widen rather than silently stranding the user on an empty inbox.
    const hasFewListings = Number.isNaN(totalListingNumber) || totalListingNumber < 5;
    const isScopedToSingleProperty = isByProperty && !!listingAlias;
    // The unread badge (StatusSelector) shows an account-wide count with no time window, so an
    // empty UNREAD-filtered list alongside a positive count means the unread conversations are all
    // older than the backend's default lookback — without widening, the UI advertises unread the
    // list can't show. An unread-filtered widen is far cheaper than the unscoped full-list lookback
    // gated above, so it's allowed regardless of listing count.
    const hasUnreadOlderThanDefaultWindow =
      readRepliedStatus === SatelliteConversationReadRepliedStatusTypeV2.UNREAD && unreadConversationCount > 0;
    const shouldFetchOlderConversations =
      !hasLoadedOlderConversations &&
      // Guard against a re-entrant widen: a counts refetch (e.g. new unread over websocket) changes
      // unreadConversationCount and re-runs this effect while the first 660-day fetch is still in
      // flight — before its resolution pushes daysBack to the URL, so hasLoadedOlderConversations
      // is still false. Without this, a second concurrent fetch and a duplicate history push fire.
      // setIsLoadingOlderConversations(true) is committed synchronously below before any such
      // re-run, so the re-run's fresh closure sees this as true and bails.
      !isLoadingOlderConversations &&
      conversations?.length === 0 &&
      (isScopedToSingleProperty || hasFewListings || hasUnreadOlderThanDefaultWindow);

    if (conversations?.length && (!conversationId || (listingAlias && isFeedId(listingAlias)))) {
      const firstConversation = conversations[0];
      changeConversation({
        conversationId: firstConversation.conversationId,
        listingAlias: firstConversation.listingDetails?.listingAlias ?? '',
        replacePath: true,
      });
    } else if (shouldFetchOlderConversations) {
      setIsLoadingOlderConversations(true);
      fetchOlderConversations();
    } else if (conversations?.length === 0) {
      if (!hasLoadedOlderConversations) {
        setHasMoreThan5Listings(true);
      }
      // The filtered list is empty and there are no older conversations to load, yet a conversation
      // is still selected from a previous view (e.g. the unread filter with nothing unread, or after
      // the last unread was read). Clear it so the message list and renter profile fall back to their
      // empty states instead of rendering a conversation that isn't in the list.
      if (conversationId) {
        clearConversation();
      }
    }
    // unreadConversationCount is a dependency because the counts query can resolve after the list
    // query settles empty — the widen decision must be re-evaluated when the count arrives.
  }, [conversationsData, unreadConversationCount]);

  const conversations = conversationsData?.conversations || [];
  const showLoadMore = !hasNextPage && !hasLoadedOlderConversations;
  const showEmptyContainer = !conversations.length || inboxPageShowEmptyContainer;

  // The list element as state, alongside listRef: the list subtree unmounts and remounts around
  // skeleton swaps while the query data keeps its identity, so the scroll-to-selected effect below
  // must re-run when the element (re)mounts — a plain ref read wouldn't re-trigger it.
  const [listElement, setListElement] = useState<HTMLUListElement | null>(null);
  const attachListRef = useCallback((element: HTMLUListElement | null) => {
    listRef.current = element;
    setListElement(element);
  }, []);

  // Identity of the current inbox query (folder + filters). buildRequestParams/getInboxConversationsQueryKey
  // mirror what useInboxConversationsQuery keys on, so this string changes exactly when that query
  // switches to a different cache entry — the signal the deep-link catch-up, the arrival-scroll
  // behavior, and the error-toast reset effect below all key off.
  const inboxQueryKeyString = JSON.stringify(
    getInboxConversationsQueryKey(
      buildRequestParams({
        conversationId: inboxPageConversationIdToFilterBy || undefined,
        listingAlias: isByProperty ? listingAlias : null,
        clientTimeZone,
        daysBack,
        folder,
        readRepliedStatus,
        renterProgressStatus,
      }),
    ),
  );

  // Deep-link catch-up bookkeeping — owned by the catch-up effect further down; read by the scroll
  // effect below to decide whether the arrival should animate.
  const deepLinkCatchUpRef = useRef({ selectionKey: '', pagesFetched: 0 });

  // Scroll the list to the URL-selected conversation — deep links (Contacts / Lead Management
  // links, refresh) land with the list at the top even when the selected item is below the fold.
  // Re-running on data/element changes matters until the item first renders (the selection can
  // arrive before its conversation loads, and before the list mounts). The guard keys on the
  // (conversation, list element) pair, not the conversation alone: a background refetch that keeps
  // the same list node must NOT re-scroll (it would yank a scroll position the user moved), but a
  // genuine remount — skeleton swap, empty-state toggle — produces a fresh node at scrollTop 0 for
  // the same selection and DOES need to re-scroll. In-list clicks are unaffected: the clicked item
  // is already visible, so `block: 'nearest'` makes the scroll a no-op. useLayoutEffect so the jump
  // happens before paint — no one-frame flash of the top of the list. A selection beyond the loaded
  // pages isn't rendered, so there's nothing to scroll to.
  const scrolledSelectionRef = useRef<{ conversationId: string; listElement: HTMLUListElement }>();
  useLayoutEffect(() => {
    if (!conversationId) {
      // Selection cleared — drop the one-shot so reselecting the same conversation on the same
      // (still-mounted) list scrolls again instead of being treated as already handled. Keeps the
      // guard self-contained rather than relying on the list unmounting whenever selection clears.
      scrolledSelectionRef.current = undefined;
      return;
    }
    if (!listElement) {
      return;
    }
    // On mobile the list stays mounted but CSS-hidden (`display: none`) while the message pane is
    // open, so it has no layout boxes. scrollIntoView is a no-op on a zero-height element; return
    // without recording so the one-shot isn't spent on the hidden list. mobileView is a dep so
    // returning to the list view (which lays it out) re-runs this and scrolls for real.
    if (listElement.clientHeight === 0) {
      return;
    }
    const alreadyScrolled =
      scrolledSelectionRef.current?.conversationId === conversationId &&
      scrolledSelectionRef.current?.listElement === listElement;
    if (alreadyScrolled) {
      return;
    }
    const items = listElement.querySelectorAll('[data-conversation-id]');
    const selectedItem = Array.from(items).find((item) => item.getAttribute('data-conversation-id') === conversationId);
    if (!selectedItem) {
      return;
    }
    scrolledSelectionRef.current = { conversationId, listElement };
    // A selection the catch-up effect had to page in animates into view — the user has been watching
    // the list load, so an instant jump reads as a snap. An already-loaded selection keeps the
    // instant pre-paint jump (the page just opens in place). Exact-key match, not just the
    // conversation: this layout effect runs before the passive catch-up effect can reset the ref, so
    // a stale budget from a previous folder/filter (or from before entering conversation search,
    // where the catch-up effect never runs) would otherwise animate an already-loaded item.
    const arrivedViaCatchUp =
      deepLinkCatchUpRef.current.pagesFetched > 0 &&
      deepLinkCatchUpRef.current.selectionKey === `${conversationId}|${inboxQueryKeyString}`;
    selectedItem.scrollIntoView({
      block: 'nearest',
      behavior: arrivedViaCatchUp && !prefersReducedMotion() ? 'smooth' : 'auto',
    });
  }, [conversationId, conversationsData, inboxQueryKeyString, listElement, mobileView]);

  // Auto-pagination from scroll/bootstrap/deep-link catch-up. After a failed next-page fetch we stop
  // — any further scroll is a no-op — rather than auto-retrying; recovery is a page refresh (prompted
  // by the error toast below) or a background refetch (realtime/poll) that clears the error.
  // fetchNextPage dedupes concurrent calls; the isFetchingNextPage check skips while one is already
  // in flight. Returns whether a fetch was actually issued so callers that budget their requests
  // (the deep-link catch-up below) only count real fetches.
  const requestNextPage = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) {
      return false;
    }
    void fetchNextPage();
    return true;
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  // Deep-link catch-up: a conversation selected via URL (Contacts / Lead Management links, refresh)
  // can be older than the loaded pages — the list query fetches newest-first and knows nothing about
  // the selection, so the item would never render (no highlight, nothing for the scroll effect above
  // to scroll to). Keep fetching pages until it appears, budgeted per (selection, query) pair: a
  // selection that never shows up under the current folder/filters would otherwise paginate the
  // whole inbox. The budget resets when the selection or the query changes so one exhausted deep
  // link doesn't disable catch-up for the next. Skipped in conversation-search mode, where the query
  // already filters to a single conversation and pagination can't surface anything new.
  useEffect(() => {
    if (!conversationId || !conversationsData || inboxPageConversationIdToFilterBy) {
      return;
    }
    const selectionKey = `${conversationId}|${inboxQueryKeyString}`;
    if (deepLinkCatchUpRef.current.selectionKey !== selectionKey) {
      deepLinkCatchUpRef.current = { selectionKey, pagesFetched: 0 };
    }
    if (deepLinkCatchUpRef.current.pagesFetched >= MAX_DEEP_LINKED_CONVERSATION_AUTO_FETCH_PAGES) {
      return;
    }
    const isSelectionLoaded = conversationsData.conversations.some(
      (conversation) => conversation.conversationId === conversationId,
    );
    if (isSelectionLoaded) {
      return;
    }
    if (requestNextPage()) {
      deepLinkCatchUpRef.current.pagesFetched++;
    }
  }, [conversationId, conversationsData, inboxPageConversationIdToFilterBy, inboxQueryKeyString, requestNextPage]);

  // Load the next page when the user scrolls near the bottom. Triggering on scroll position (rather
  // than the skeleton's visibility) advances pagination on every scroll-down and never fires while
  // idle, so it can't loop the request when the list sits at the bottom of a short viewport.
  // Throttled because onScroll fires many times per second; read the metrics from listRef rather
  // than the event, since the trailing call runs after the synthetic event has been released.
  const handleListScroll = useMemo(
    () =>
      throttle(() => {
        const list = listRef.current;
        if (!list) {
          return;
        }
        const { scrollTop, scrollHeight, clientHeight } = list;
        if (scrollHeight - scrollTop - clientHeight <= NEXT_PAGE_SCROLL_THRESHOLD_PX) {
          requestNextPage();
        }
      }, NEXT_PAGE_SCROLL_THROTTLE_MS),
    [requestNextPage],
  );

  // Cancel any pending trailing scroll callback when the handler is replaced or on unmount.
  useEffect(() => () => handleListScroll.cancel(), [handleListScroll]);

  // Bootstrap pagination: the scroll handler only fires on a real scroll, so if the first page
  // doesn't fill the list there's no scrollbar and later pages would be unreachable (the "Load older
  // conversations" button only shows once hasNextPage is false). While more pages exist and the list
  // isn't tall enough to scroll, fetch until it overflows. Bounded on success once scrollHeight
  // exceeds clientHeight; requestNextPage's stop-on-error guard prevents a loop on failure (the error
  // toast below prompts a refresh to recover).
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || !(list.clientHeight > 0 && list.scrollHeight <= list.clientHeight)) {
      return;
    }
    requestNextPage();
  }, [conversations.length, requestNextPage]);

  // Whether the user manually dismissed the load-more error toast during the current error episode,
  // so we don't immediately re-enqueue it while the error persists. Reset once real recovery is
  // confirmed (see nextPageRecoveredRef below), not merely once the error flag clears.
  const loadMoreErrorToastDismissedRef = useRef(false);

  // Whether we've enqueued a toast that hasn't been dismissed yet (by us or the user). Tracked
  // ourselves rather than derived by searching `toasts`: the ToastProvider's `toasts` only reflects
  // *active* toasts — a toast can sit invisible in its internal pending queue (e.g. 3 unrelated
  // toasts already showing, the library default `maxSize`), where a key lookup can't find it and
  // would wrongly enqueue a duplicate.
  const loadMoreErrorToastEnqueuedRef = useRef(false);

  // Whether the missing next page has actually loaded (or there's nothing left to load) since the
  // current error toast was shown. Persists across renders so it survives the toast being pending
  // (see loadMoreErrorToastEnqueuedRef) until it becomes active and can actually be dismissed.
  const nextPageRecoveredRef = useRef(false);

  // Previous render's isFetchingNextPage, so the effect below can detect the moment a next-page
  // fetch attempt finishes (see justFinishedFetchingNextPage).
  const wasFetchingNextPageRef = useRef(false);

  // The error toast is scoped to one inbox query (one folder + filter combination). When the user
  // switches folder/filters the query key changes and the toast — which described the previous
  // query's pagination — is stale; ConversationList isn't remounted on that switch (same inbox
  // route), so nothing else clears it. Drop it and reset the per-episode state so the new query
  // starts clean (and a lingering manual-dismiss doesn't suppress a genuine failure in the new one).
  const inboxQueryKeyRef = useRef(inboxQueryKeyString);
  useEffect(() => {
    if (inboxQueryKeyRef.current === inboxQueryKeyString) {
      return;
    }
    inboxQueryKeyRef.current = inboxQueryKeyString;
    loadMoreErrorToastEnqueuedRef.current = false;
    loadMoreErrorToastDismissedRef.current = false;
    nextPageRecoveredRef.current = false;
    const staleToast = toasts.find((toast) => toast?.node?.key === LOAD_MORE_ERROR_TOAST_KEY);
    if (staleToast) {
      dismiss(staleToast.id);
    }
  }, [inboxQueryKeyString, toasts, dismiss]);

  // Surface a persistent, dismissible toast when auto-pagination fails. Loading more is passive
  // (driven by scroll, no button), so a transient toast is easy to miss and would recreate the
  // "looks like the end of the list" confusion; it persists until the user dismisses it or the
  // missing page actually loads. Recovery is a page refresh — we don't auto-retry, and don't offer
  // an in-place retry, since a 4xx would just re-hit a forbidden endpoint.
  //
  // Dismissal is gated on more than `!isFetchNextPageError`: React Query flips that flag to false
  // the instant ANY refetch starts (see useInboxConversationsQuery's shared query, and the
  // background refetch() that useRealtimeUpdates fires on every WS event / reconnect / 60s poll
  // tick) — regardless of that refetch's outcome, and regardless of whether it ever re-attempts the
  // missing next page (a plain refetch() only re-fetches already-loaded pages). Without the extra
  // check below, routine inbox activity would silently clear the toast while the user is still
  // stuck one page behind, recreating the exact silent failure this toast exists to surface.
  useEffect(() => {
    const justFinishedFetchingNextPage = wasFetchingNextPageRef.current && !isFetchingNextPage;
    wasFetchingNextPageRef.current = isFetchingNextPage;

    if (isFetchNextPageError) {
      nextPageRecoveredRef.current = false;
      if (loadMoreErrorToastEnqueuedRef.current || loadMoreErrorToastDismissedRef.current) {
        return;
      }
      loadMoreErrorToastEnqueuedRef.current = true;
      enqueueToast(
        <Toast
          key={LOAD_MORE_ERROR_TOAST_KEY}
          tone="critical"
          onClose={() => {
            loadMoreErrorToastDismissedRef.current = true;
            loadMoreErrorToastEnqueuedRef.current = false;
          }}
        >
          {LOAD_MORE_ERROR_MESSAGE}
        </Toast>,
        { duration: 0 }, // persist until dismissed or the missing page actually loads
      );
      return;
    }

    if (justFinishedFetchingNextPage || !hasNextPage) {
      nextPageRecoveredRef.current = true;
      loadMoreErrorToastDismissedRef.current = false;
    }

    if (!loadMoreErrorToastEnqueuedRef.current || !nextPageRecoveredRef.current) {
      return;
    }

    const existingToast = toasts.find((toast) => toast?.node?.key === LOAD_MORE_ERROR_TOAST_KEY);
    if (!existingToast) {
      // Still pending (invisible in `toasts`) — nothing to dismiss yet. `toasts` is a dependency
      // below, so promotion to active re-runs this effect and we'll dismiss it then.
      return;
    }
    dismiss(existingToast.id);
    loadMoreErrorToastEnqueuedRef.current = false;
  }, [isFetchNextPageError, isFetchingNextPage, hasNextPage, toasts, enqueueToast, dismiss]);

  // Keep the latest toast-queue accessors in a ref so the unmount cleanup below can read current
  // state without re-running (and dismissing the toast) every time the queue changes.
  const toastQueueRef = useRef({ toasts, dismiss });
  useEffect(() => {
    toastQueueRef.current = { toasts, dismiss };
  });

  // Dismiss the error toast if the inbox unmounts while it's still showing. The toast lives in the
  // app-level ToastProvider, which outlives this route, so without this it would linger on whatever
  // page the user navigates to next. Best-effort: like the lookup above, this can't find the toast
  // while it's still pending (not yet promoted to active) — a pre-existing limitation of the
  // ToastProvider API, which never hands back the enqueued toast's id.
  useEffect(
    () => () => {
      const { toasts: latestToasts, dismiss: dismissToast } = toastQueueRef.current;
      const staleToast = latestToasts.find((toast) => toast?.node?.key === LOAD_MORE_ERROR_TOAST_KEY);
      if (staleToast) {
        dismissToast(staleToast.id);
      }
    },
    [],
  );

  if (shouldShowSkeleton) {
    return <ConversationListSkeleton />;
  }

  if ((loading || isLoadingOlderConversations) && conversations.length === 0) {
    inboxPageIsLoadingStore.set(true);
    return <ConversationListSkeleton />;
  }

  if (error && !conversations.length) {
    logError({
      error,
      context: {
        details: 'ConversationList - Error loading conversations for conversations list',
        listingAlias: isByProperty ? listingAlias : null,
        clientTimeZone,
        daysBack: hasLoadedOlderConversations ? MAX_OLD_CONVERSATION_DAYS : null,
        folder,
        renterProgressStatus,
        readRepliedStatus,
        numberOfConversations: conversations.length,
      },
    });
  }

  async function fetchOlderConversations() {
    if (!isLoadingOlderConversations) {
      setIsLoadingOlderConversations(true);
    }

    const navTokenAtFetchStart = navTokenRef.current;
    const baseParams = buildRequestParams({
      listingAlias: isByProperty ? listingAlias : null,
      clientTimeZone,
      daysBack: MAX_OLD_CONVERSATION_DAYS,
      folder,
      readRepliedStatus,
      renterProgressStatus,
    });
    const lastConversationId = conversations.length
      ? conversations[conversations.length - 1].conversationId
      : undefined;

    try {
      const olderResult = await SatelliteWebLandlordConversationHandler_getLatestConversationsForInbox({
        ...baseParams,
        beforeConversationId: lastConversationId,
      });
      const newQueryKey = getInboxConversationsQueryKey(baseParams);
      queryClient.setQueryData<InfiniteData<SatelliteWebLandlordGetLatestConversationsForInboxResponseDto>>(
        newQueryKey,
        {
          // Coerce a missing `conversations` array so a degenerate older-conversations response
          // can't write a page that later readers would choke on.
          pages: [
            { conversations, hasNextPage: true },
            { ...olderResult, conversations: olderResult.conversations ?? [] },
          ],
          pageParams: [undefined, lastConversationId],
        },
      );

      // If any URL-bearing context changed while we were awaiting (folder,
      // selected conversation, filters, mobile view), skip the URL push — the
      // captured closure values are stale and would clobber the user's new
      // navigation state.
      if (navTokenRef.current === navTokenAtFetchStart) {
        updatePath({
          conversationId,
          listingAlias,
          byProperty: isByProperty,
          filtersQueryString: getFiltersQueryString({
            byProperty: isByProperty,
            daysBack: MAX_OLD_CONVERSATION_DAYS,
            folder,
            mobileView: mobileView || InboxPageMobileView.ConversationList,
            readRepliedStatus,
            renterProgressStatus,
          }),
        });
      }
    } catch (error) {
      logError({
        error,
        context: {
          details: 'ConversationList - Error fetching older conversations',
        },
      });
    }

    inboxPageIsLoadingStore.set(false);
    setIsLoadingOlderConversations(false);
  }

  const handleFetchOlderConversations = () => fetchOlderConversations();

  const handleConversationSelect = async ({
    selectedConversationId,
    selectedListingAlias,
  }: ConversationSelectArgs): Promise<void> => {
    if (selectedConversationId === conversationId && isDesktopView) {
      return;
    }

    emitConversationItemSelectEvent(user, { listingAlias: selectedListingAlias });

    changeConversation({
      conversationId: selectedConversationId,
      listingAlias: selectedListingAlias,
      newMobileView: InboxPageMobileView.MessageList,
    });
  };

  return (
    <>
      {isEnterpriseCustomer && <ViewLeads />}

      {showEmptyContainer ? (
        <EmptyContainer
          folder={folder}
          hasConversationSearchText={!!inboxPageConversationSearchText}
          hasMoreThan5Listings={hasMoreThan5Listings}
          isByProperty={isByProperty}
          readRepliedStatus={readRepliedStatus}
          renterProgressStatus={renterProgressStatus}
          showErrorMessage={!!error}
        />
      ) : (
        <List.Root
          ref={attachListRef}
          aria-label="conversations"
          data-testid="conversation-list"
          appearance="none"
          onScroll={handleListScroll}
          css={{
            overflowY: 'auto',
            ...scrollbarHoverStyles,
          }}
        >
          <ConditionalWrapper
            shouldRenderWrapperElement={isMobileView}
            wrapper={(children) => <SwipeableList threshold={0.75}>{children}</SwipeableList>}
          >
            <>
              {conversations.map((conversation) => (
                <ConversationItemContainer
                  key={conversation.conversationId}
                  conversation={conversation}
                  onSelect={handleConversationSelect}
                />
              ))}

              {isFetchingNextPage && (
                <>
                  <ConversationItemSkeleton />
                  <ConversationItemSkeleton />
                </>
              )}

              {showLoadMore && (
                <Box
                  css={{
                    paddingBlock: 'default',
                    textAlign: 'center',
                  }}
                >
                  <TextButton
                    onClick={handleFetchOlderConversations}
                    disabled={isLoadingOlderConversations}
                    css={{ textAlign: 'center' }}
                  >
                    {isLoadingOlderConversations ? 'Loading older conversations...' : 'Load older conversations'}
                  </TextButton>
                </Box>
              )}
            </>
          </ConditionalWrapper>
        </List.Root>
      )}
    </>
  );
};

export default ConversationList;
